import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Select, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik, FormikProps } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { HttpStatusCode } from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE, ROLES } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectShip from "@/components/Selects/SelectShip";
import { RootState } from "@/redux/store";
import { useAppSelector } from "@/redux/hooks";
import { addSIRESchema } from "@/schemas/addSIRESchema";
import { editSIRESchema } from "@/schemas/editSIRESchema";
import { getDataVesselSire, getSireReviewById, postSireReview, postSireReviewReCreate, SireReview } from "@/services/sireReviewService";
import { SingleValue } from "react-select";
import { Vessel } from "@/services/shipOwnerService";
import EditFile from "@/components/Fields/EditFile";
import { Option } from "@/utils/select";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { getSettingsVessel } from "@/services/settingService";
import { getDataRefRequestor, RefData } from "@/services/refDataService";

const DEFAULT_AGE = 20;
const DEFAULT_DWT = 20000;

type FormContents = {
    requestor: string,
    pic: string,
    shipId: string,
    shipName: string,
    imoNumber: string,
    vesselType: string,
    reviewDate: string,
    addAld: string,
    classSurveyStatus: File | string | null,
    capSurveyReport: File | string | null,
    certificates: File | string | null,
    other: File | string | null,
    classSurveyStatusDate: string,
    capSurveyReportDate: string,
    certificatesDate: string,
    otherDate: string,
    dwt: number,
    shipBuildDate: string | Date,
    notes: string | null,
};

const initialValues: FormContents = {
    requestor: '',
    pic: '',
    shipId: '',
    shipName: '',
    imoNumber: '',
    vesselType: '',
    reviewDate: format(new Date(), FORMAT_ADD_DATE) || '',
    addAld: '',
    classSurveyStatus: null,
    capSurveyReport: null,
    certificates: null,
    other: null,
    classSurveyStatusDate: '',
    capSurveyReportDate: '',
    certificatesDate: '',
    otherDate: '',
    dwt: 0,
    shipBuildDate: '',
    notes: '',
};

const AddSireReviewRequestor = () => {
    const navigate = useNavigate();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();

    const { id, isReRequest } = location.state || {};
    const appId = useAppSelector((state: RootState) => state.account.appId);

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState<FormContents>(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [listShipName, setListShipName] = useState<Option[]>([]);
    const [listRawShipName, setListRawShipName] = useState<Vessel[]>([]);
    const [previousData, setPreviousData] = useState<FormContents>(initialValues);
    const [dwt, setDwt] = useState<number>(0);
    const [maxShipAgeYears, setMaxShipAgeYears] = useState<number>(DEFAULT_AGE);
    const [maxDwt, setMaxDwt] = useState<number>(DEFAULT_DWT);
    const [requestorOptions, setRequestorOptions] = useState<RefData[]>([]);
    const [isLoadingRequestor, setIsLoadingRequestor] = useState(false);
    const [requestorError, setRequestorError] = useState("");
    //const [previousDataRaw, setPreviousDataRaw] = useState<SireReview | null>(null);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Add SIRE Review?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: FormContents) => {
        setTitle(!isReRequest ? "Add SIRE Review?" : "Re-Request SIRE Review?");
        setConfirmationTest(!isReRequest ? "Are you sure you want to add?" : "Are you sure you want to re-request?");
        setConfirmationTitleButton(!isReRequest ? "Add SIRE Review" : "Re-Request SIRE Review");
        onOpen();
        setDataAdd(data);
    }

    const generateDataAdd = () => {
        //const vesselName = listShipName?.find((ship: Option) => ship.value === dataAdd.shipId)?.label;

        const data = new FormData();
        data.append('SireReviewId', id || '');
        data.append('RequestorId', appId);
        data.append('Requestor', dataAdd.requestor);
        data.append('PIC', dataAdd.pic);
        data.append('VesselId', dataAdd.shipId);
        data.append('VesselName', dataAdd.shipName || '');
        data.append('IMONumber', dataAdd.imoNumber);
        data.append('ADDALDPertamina', dataAdd.addAld)
        data.append(
            'ReviewDate',
            previousData.reviewDate
                ? format(new Date(previousData.reviewDate), FORMAT_ADD_DATE)
                : format(new Date(dataAdd?.reviewDate ?? Date.now()), FORMAT_ADD_DATE)
        );

        if (Array.isArray(dataAdd.classSurveyStatus)) {
            dataAdd.classSurveyStatus.forEach((file: File) => {
                data.append('ClassSurveyStatusFile', file);
            });
        } else if (dataAdd.classSurveyStatus) {
            data.append('ClassSurveyStatusFile', dataAdd.classSurveyStatus);
        } else {
            data.append('ClassSurveyStatusFile', '');
        }

        if (Array.isArray(dataAdd.capSurveyReport)) {
            dataAdd.capSurveyReport.forEach((file: File) => {
                data.append('CAPSurveyReportFile', file);
            });
        } else if (dataAdd.capSurveyReport) {
            data.append('CAPSurveyReportFile', dataAdd.capSurveyReport);
        } else {
            data.append('CAPSurveyReportFile', '');
        }

        if (Array.isArray(dataAdd.certificates)) {
            dataAdd.certificates.forEach((file: File) => {
                data.append('AllStatuoryCertificatesFile', file);
            });
        } else if (dataAdd.certificates) {
            data.append('AllStatuoryCertificatesFile', dataAdd.certificates);
        } else {
            data.append('AllStatuoryCertificatesFile', '');
        }

        if (Array.isArray(dataAdd.other)) {
            dataAdd.other.forEach((file: File) => {
                data.append('OtherFile', file);
            });
        } else if (dataAdd.other) {
            data.append('OtherFile', dataAdd.other);
        } else {
            data.append('OtherFile', '');
        }

        data.append('ClassSurveyStatusDateTime', dataAdd.classSurveyStatusDate ? format(dataAdd.classSurveyStatusDate, FORMAT_ADD_DATE) : '');
        data.append('CAPSurveyReportDateTime', dataAdd.capSurveyReportDate ? format(dataAdd.capSurveyReportDate, FORMAT_ADD_DATE) : '');
        data.append('AllStatuoryCertificatesDateTime', dataAdd.certificatesDate ? format(dataAdd.certificatesDate, FORMAT_ADD_DATE) : '');
        data.append('OtherFileDateTime', dataAdd.otherDate ? format(dataAdd.otherDate, FORMAT_ADD_DATE) : '');
        data.append('Notes', dataAdd.notes ?? '');
        if (id) {
            data.append('ClassSurveyStatusId', previousData.classSurveyStatus ?? '');
            data.append('CAPSurveyReportId', previousData.capSurveyReport ?? '');
            data.append('AllStatuoryCertificatesId', previousData.certificates ?? '');
            data.append('OtherId', previousData.other ?? '');
        }
        return data;
    }

    const handleConfirm = async () => {
        console.log('%chandleConfirm', 'color: green', dataAdd);
        onClose();

        if (title === "Cancel to Add SIRE Review?") {
            navigate('/manage-sire-review');
            return;
        }

        setIsSubmitting(true);
        try {
            const obj = generateDataAdd();
            const postFunction = id ? postSireReviewReCreate : postSireReview;
            const response = await postFunction(obj);

            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'SIRE review created.',
                    description: "We've created SIRE review for you.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });

                setTimeout(() => {
                    navigate('/manage-sire-review')
                }, 1000);
            } else {
                toast({
                    title: 'SIRE review failed to create.',
                    description: "We unabled to create SIRE review for you.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: (fieldName: string, files: File[] | File) => void, fieldName: string) => {
        setFieldValue(fieldName, acceptedFiles);
    };

    const handleChangeShip = (value: string, props: FormikProps<FormContents>) => {
        if (value) {
            const selectedShip = listRawShipName.find((x: Vessel) => x.vesselId === value);
            props.setFieldValue('shipId', value);
            props.setFieldValue('shipName', selectedShip?.vesselName || '');
            props.setFieldValue('imoNumber', selectedShip?.imoreg || '');
            //console.log(value)
            //console.log(imoRegNumber)
        } else {
            props.setFieldValue('shipId', '');
            props.setFieldValue('shipName', '');
            props.setFieldValue('imoNumber', '');
        }
    }

    const getListVessel = async () => {
        const response = await getDataVesselSire();
        if (response?.status === HttpStatusCode.Ok) {
            const formattedData = response?.data?.map((vessel: Vessel) => ({
                value: vessel.vesselId,
                label: vessel.vesselName + ' (' + vessel.imoreg + ')',
            })) || [];
            setListRawShipName(response.data);
            setListShipName(formattedData);
        }
    };

    const getDetailSire = async () => {
        const response = await getSireReviewById(id);
        if (response.status === HttpStatusCode.Ok) {
            const sireDetail = response.data as SireReview;
            //console.log(sireDetail)
            //setPreviousDataRaw(sireDetail);
            setPreviousData({
                requestor: sireDetail.requestor ?? '',
                pic: sireDetail.pic ?? '',
                shipId: sireDetail.vesselId ?? '',
                shipName: sireDetail.vesselName ?? '',
                imoNumber: sireDetail.imoNumber ?? '',
                vesselType: sireDetail.vesselType ?? '',
                reviewDate: sireDetail.reviewDate ? format(new Date(sireDetail.reviewDate), FORMAT_ADD_DATE) : '',
                addAld: sireDetail.addaldPertamina ?? '',
                classSurveyStatus: sireDetail.classSurveyStatusId,
                capSurveyReport: sireDetail.capSurveyReportId,
                certificates: sireDetail.allStatuoryCertificatesId,
                other: sireDetail.othersId,
                classSurveyStatusDate: sireDetail.classSurveyStatusDate ? format(new Date(sireDetail.classSurveyStatusDate), FORMAT_ADD_DATE) : '',
                capSurveyReportDate: sireDetail.capSurveyReportDate ? format(new Date(sireDetail.capSurveyReportDate), FORMAT_ADD_DATE) : '',
                certificatesDate: sireDetail.allStatuoryCertificatesDate ? format(new Date(sireDetail.allStatuoryCertificatesDate), FORMAT_ADD_DATE) : '',
                otherDate: sireDetail.othersDate ? format(new Date(sireDetail.othersDate), FORMAT_ADD_DATE) : '',
                dwt: sireDetail.dwt ?? '',
                shipBuildDate: sireDetail.shipBuildDate ? format(new Date(sireDetail.shipBuildDate), FORMAT_ADD_DATE) : '',
                notes: sireDetail.notes ?? '',
            });
        }
    }

    const getShipAgeDwt = async () => {
        const response = await getSettingsVessel();
        if (response?.status === HttpStatusCode.Ok) {
            const settingsAge = Number(response.data?.vesselYearsBuild || DEFAULT_AGE);
            const settingsDwt = Number(response.data?.vesselDWT || DEFAULT_DWT);
            setMaxShipAgeYears(settingsAge);
            setMaxDwt(settingsDwt);
        }
    }

    useEffect(() => {
        getListVessel();
        getShipAgeDwt();
        (async () => {
            setIsLoadingRequestor(true);
            setRequestorError("");
            try {
                const response = await getDataRefRequestor('requestor') as any;
                if (response?.status === HttpStatusCode.Ok) {
                    setRequestorOptions(response?.data || []);
                } else {
                    setRequestorError(response?.message || 'Failed to fetch requestor list');
                }
            } catch (e: any) {
                setRequestorError(e?.message || 'Failed to fetch requestor list');
            } finally {
                setIsLoadingRequestor(false);
            }
        })();
    }, [])

    useEffect(() => {
        if (id) {
            getListVessel().then(() => {
                getDetailSire();
            })
        }
    }, [id]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={previousData}
                    enableReinitialize
                    validationSchema={() => (
                        id && [ROLES.REQUESTOR, ROLES.SUPER_ADMIN].includes(role)
                            ? editSIRESchema(maxShipAgeYears, maxDwt)
                            : addSIRESchema(maxShipAgeYears, maxDwt)
                    )}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                >
                    {(props: FormikProps<FormContents>) => (
                        <Form>
                            {/* <Field name='requestor'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.requestor && form.touched.requestor)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Requestor<span style={{ color: 'red' }}>*<br /></span><em style={{ color: '#344054', fontSize: '12px' }}>(Company Name)</em></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Requestor' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.requestor)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field> */}
                            <Field name='requestor'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.requestor && form.touched.requestor) || Boolean(requestorError)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="requestor" className="form-label">Requestor<span style={{ color: 'red' }}>*<br /></span><em style={{ color: '#344054', fontSize: '12px' }}>(Company Name)</em></FormLabel>
                                            <div className="form-input-container">
                                                <Select
                                                    id="requestor"
                                                    placeholder={isLoadingRequestor ? 'Loading requestors...' : 'Select Requestor'}
                                                    // value={props.values.requestor}
                                                    {...field}
                                                    onChange={(e: any) => props.setFieldValue('requestor', e.target.value)}
                                                    isDisabled={isLoadingRequestor || isSubmitting || !!previousData.requestor}
                                                    aria-busy={isLoadingRequestor}
                                                >
                                                    {requestorOptions.map((opt) => (
                                                        <option key={opt.id} value={opt.name} {...(opt.name == field.value ? { selected: true } : {})}>{opt.name}</option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>{String(requestorError || form.errors.requestor || '')}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='pic'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.pic && form.touched.pic)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Person in Charge (PIC)<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input PIC' className="form-input" disabled={isSubmitting || !!previousData.pic} />
                                                <FormErrorMessage>{String(form.errors.pic)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='shipId'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.shipId && form.touched.shipId)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Select Vessel</FormLabel>
                                            <div className="form-input-container">
                                                <SelectShip
                                                    placeholder="Choose Ship" name='shipId'
                                                    handleChange={(change: SingleValue<Option>) => handleChangeShip(change?.value ?? '', props)}
                                                    value={props.values.shipId} options={listShipName}
                                                    hasError={Boolean(form.errors.shipId && form.touched.shipId)}
                                                    isDisabled={isSubmitting || !!previousData.shipId}
                                                    isClearable
                                                />
                                                <FormErrorMessage>{String(form.errors.shipId)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='shipName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.shipName && form.touched.shipName)} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Vessel Name' className="form-input" disabled={isSubmitting || Boolean(props.values.shipId)} />
                                                <FormErrorMessage>{String(form.errors.shipName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='imoNumber'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.imoNumber && form.touched.imoNumber)} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">IMO Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input IMO Number' className="form-input" disabled={isSubmitting || Boolean(props.values.shipId)} />
                                                <FormErrorMessage>{String(form.errors.imoNumber)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='vesselType'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.vesselType && form.touched.vesselType)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Type</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.vesselType)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name="shipBuildDate">
                                {({ field, form }: FieldProps) => {
                                    const isMandatory = !isReRequest; // sama persis dengan DWT

                                    const isError = Boolean(
                                        form.errors.shipBuildDate && form.touched.shipBuildDate
                                    );

                                    return (
                                        <FormControl isInvalid={isError} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">
                                                    Vessel Build Date {isMandatory && <span style={{ color: "red" }}>*</span>}
                                                </FormLabel>

                                                <div className={isError ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                    <DatePicker
                                                        id="shipBuildDate"
                                                        selected={field.value ?? null}
                                                        onChange={(date: Date | null) =>
                                                            props.setFieldValue("shipBuildDate", date)
                                                        }
                                                        dateFormat="dd-MM-yyyy"
                                                        placeholderText="Build Date"
                                                        maxDate={new Date()}
                                                        showYearDropdown
                                                        scrollableYearDropdown
                                                        yearDropdownItemNumber={100}
                                                        disabled={isSubmitting}
                                                    />

                                                    {isError && (
                                                        <FormErrorMessage className="form-date-picker-error-message">
                                                            {String(form.errors.shipBuildDate)}
                                                        </FormErrorMessage>
                                                    )}
                                                </div>
                                            </div>
                                        </FormControl>
                                    );
                                }}
                            </Field>

                            <Field name='dwt'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.dwt && form.touched.dwt)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel DWT {!isReRequest && (<span style={{ color: 'red' }}>*</span>)}</FormLabel>
                                            <div className="form-input-container">
                                                <div className="form-input">
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Input Vessel DWT'
                                                        prefix=''
                                                        value={dwt}
                                                        error={Boolean(form.errors.dwt && form.touched.dwt)}
                                                        onValueChange={({ floatValue = 0 }) => {
                                                            setDwt(floatValue);
                                                            props.setFieldValue('dwt', floatValue);
                                                        }}
                                                        disabled={isSubmitting || isReRequest}
                                                        width={'12em'}
                                                        decimalScale={0}
                                                    />
                                                    <span style={{ marginLeft: 5 }}>Tonnes</span>
                                                </div>
                                                <FormErrorMessage>{String(form.errors.dwt)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='reviewDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reviewDate && form.touched.reviewDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Review Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className={form.errors.reviewDate && form.touched.reviewDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="reviewDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('reviewDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Review Date"
                                                    disabled={isSubmitting || !!previousData.reviewDate}
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.reviewDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='addAld'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.addAld && form.touched.addAld)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">ADD / ALD<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input ADD ALD' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.addAld)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name='classSurveyStatus'>
                                {({ form }: FieldProps) => {
                                    return (
                                        <FormControl isInvalid={Boolean(form.errors.classSurveyStatus && form.submitCount)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Class Survey Status<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        handleDrop={(e) => handleDrop(e, props.setFieldValue, 'classSurveyStatus')}
                                                        isInvalid={Boolean(form.errors.classSurveyStatus && form.submitCount)}
                                                        isDisabled={isSubmitting}
                                                        type="sire-review"
                                                        fileId={typeof (previousData.classSurveyStatus) === 'string' ? previousData.classSurveyStatus : undefined}
                                                        isDownloadFolder={false}
                                                        title="Class Survey Status"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.classSurveyStatus)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    );
                                }}
                            </Field>
                            <Field name='classSurveyStatusDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.classSurveyStatusDate && form.touched.classSurveyStatusDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label"></FormLabel>
                                            <div className={form.errors.classSurveyStatusDate && form.touched.classSurveyStatusDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="classSurveyStatusDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('classSurveyStatusDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Class Survey Status Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.classSurveyStatusDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='capSurveyReport'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.capSurveyReport && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <div className="form-label form-label-with-info">
                                                <FormLabel>CAP/CAS Survey Report</FormLabel>
                                                <span style={{ fontSize: '14px' }}>CAP for Hull, Machinery, & Cargo</span>
                                            </div>
                                            <div className="form-input-container">
                                                <EditFile
                                                    handleDrop={(e) => handleDrop(e, props.setFieldValue, 'capSurveyReport')}
                                                    isInvalid={Boolean(form.errors.capSurveyReport && form.submitCount)}
                                                    isDisabled={isSubmitting}
                                                    type="sire-review"
                                                    fileId={typeof (previousData.capSurveyReport) === 'string' ? previousData.capSurveyReport : undefined}
                                                    isDownloadFolder={false}
                                                    title="CAP/CAS Survey Report"
                                                />
                                                <FormErrorMessage>{String(form.errors.capSurveyReport)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='capSurveyReportDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.capSurveyReportDate && form.touched.capSurveyReportDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label"></FormLabel>
                                            <div className={form.errors.capSurveyReportDate && form.touched.capSurveyReportDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="capSurveyReportDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('capSurveyReportDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="CAP/CAS Survey Report Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.capSurveyReportDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='certificates'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.certificates && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">All Statutory Certificates</FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    handleDrop={(e) => handleDrop(e, props.setFieldValue, 'certificates')}
                                                    isInvalid={Boolean(form.errors.certificates && form.submitCount)}
                                                    isDisabled={isSubmitting}
                                                    type="sire-review"
                                                    fileId={typeof (previousData.certificates) === 'string' ? previousData.certificates : undefined}
                                                    isDownloadFolder={false}
                                                    title="All Statutory Certificates"
                                                />
                                                <FormErrorMessage>{String(form.errors.certificates)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='certificatesDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.certificatesDate && form.touched.certificatesDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label"></FormLabel>
                                            <div className={form.errors.certificatesDate && form.touched.certificatesDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="certificatesDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('certificatesDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="All Statutory Certificates Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.certificatesDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='other'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.other && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Other</FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    handleDrop={(e) => handleDrop(e, props.setFieldValue, 'other')}
                                                    isInvalid={Boolean(form.errors.other && form.submitCount)}
                                                    isDisabled={isSubmitting}
                                                    type="sire-review"
                                                    fileId={typeof (previousData.other) === 'string' ? previousData.other : undefined}
                                                    isDownloadFolder={false}
                                                    title="Other"
                                                />
                                                <FormErrorMessage>{String(form.errors.other)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='otherDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.otherDate && form.touched.otherDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label"></FormLabel>
                                            <div className={form.errors.otherDate && form.touched.otherDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="otherDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('otherDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Other File Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.otherDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            {id && [ROLES.REQUESTOR, ROLES.SUPER_ADMIN].includes(role) &&
                                <Field name='notes'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.notes && form.touched.notes)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Notes<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Input {...field} placeholder='Input Notes (required)' className="form-input" />
                                                    <FormErrorMessage>{String(form.errors.notes)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    {(!id ? 'Add SIRE Review' : 'Re-request SIRE Review')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default AddSireReviewRequestor;
